import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuItem,
} from "@radix-ui/react-dropdown-menu";
import { useKeycloak } from "@react-keycloak/web";
import { toast } from '../../components/ui-components/Toast';
import "../DropdownMenu.css";

const API_URL = process.env.REACT_APP_API_URL;

const GeneralSubmenu = ({ onFilterChange, currentFilter, reportId, reportName, viewFilters, onSchedule, triggerClassName }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
	const { keycloak } = useKeycloak();

	const filterOptions = [
		// { value: "edit-dashboard", label: "Edit Dashboard", icon: icon({ name: "pencil", family: "sharp", style: "regular" }) },
		// { value: "all", label: "All" },
		// { value: "tags", label: "Tags" },
		// { value: "virtual-tags", label: "Virtual Tags" },
		// { value: "cost-resources", label: "Cost Resources" },
		// { value: "projects", label: "Projects" },
	];

	const handleFilterChange = (value) => {
		onFilterChange(value);
		setIsOpen(false);
	};

	// Update cursor style when generating PDF
	useEffect(() => {
		if (isGeneratingPdf) {
			document.body.style.cursor = "wait";
		} else {
			document.body.style.cursor = "";
		}

		// Cleanup: restore cursor on unmount
		return () => {
			document.body.style.cursor = "";
		};
	}, [isGeneratingPdf]);

	const handleGeneratePdf = useCallback(async (event) => {
		if (event) {
			event.preventDefault();
		}
		
		if (!reportId || !keycloak?.token) {
			console.error("Report ID or authentication token is missing");
			return;
		}

		setIsGeneratingPdf(true);
		setIsOpen(false);

		try {
			// Build URL with view filters if they exist
			const baseUrl = `${API_URL}/generate-report/${reportId}`;
			console.log('GeneralSubmenu: viewFilters prop:', viewFilters);
			console.log('GeneralSubmenu: viewFilters type:', typeof viewFilters);
			console.log('GeneralSubmenu: viewFilters isArray:', Array.isArray(viewFilters));
			console.log('GeneralSubmenu: viewFilters length:', viewFilters?.length);
			
			// Filter out empty view filters (those with no selectedValues)
			const validViewFilters = viewFilters && Array.isArray(viewFilters) 
				? viewFilters.filter(f => {
					const values = f.selectedValues || f.selected_values || [];
					return values.length > 0;
				})
				: [];
			
			console.log('GeneralSubmenu: validViewFilters after filtering:', validViewFilters);
			
			const url = validViewFilters.length > 0
				? `${baseUrl}?view_filters=${encodeURIComponent(JSON.stringify(validViewFilters))}`
				: baseUrl;
			console.log('GeneralSubmenu: Generated URL:', url.substring(0, 200)); // Log first 200 chars to avoid huge logs
			
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${keycloak.token}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to generate PDF: ${response.status}`);
			}

			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);
			
			// Create a safe filename from report name
			// Remove or replace invalid filename characters
			const safeReportName = reportName 
				? reportName.replace(/[<>:"/\\|?*]/g, '').trim() || 'Report'
				: 'Report';
			const filename = `Report ${safeReportName}.pdf`;
			
			// Create a temporary anchor element to trigger download
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			
			// Clean up: remove the link and revoke the blob URL
			link.remove();
			URL.revokeObjectURL(blobUrl);

			// Show success toast
			toast.success("Report generated successfully", {
				description: `Your PDF report has been downloaded as ${filename}.`,
			});
		} catch (error) {
			console.error("Error generating PDF:", error);
			toast.error("Failed to generate PDF", {
				description: "Please try again.",
			});
		} finally {
			setIsGeneratingPdf(false);
		}
	}, [reportId, keycloak?.token, reportName, viewFilters]);

	const handleSchedule = useCallback((event) => {
		event.preventDefault();
		setIsOpen(false);
		onSchedule?.();
	}, [onSchedule]);

	// Nothing to show (no filter options and not a report context) → render nothing
	// instead of an empty chevron dropdown.
	const hasContent = filterOptions.length > 0 || !!reportId;
	if (!hasContent) {
		return null;
	}

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						className={triggerClassName || "flex items-center justify-center w-8 h-8 rounded-full bg-side border border-solid border-border hover:border-border-strong transition-colors cursor-pointer"}
						style={triggerClassName ? undefined : {
							minWidth: 36,
							minHeight: 36,
							width: 36,
							height: 36,
							padding: 0,
						}}
						disabled={isGeneratingPdf}
					>
                        <FontAwesomeIcon
                            icon={icon({ name: "angle-down", family: "sharp", style: "solid" })}
                            className={`${triggerClassName ? "text-[#334155]" : "text-text-2"} text-base`}
                            style={{ fontSize: "1rem" }}
                        />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent 
					className="DropdownMenuContent w-[200px]" 
					side="bottom" 
					align="end"
					sideOffset={5}
				>
					<DropdownMenuRadioGroup value={currentFilter} onValueChange={handleFilterChange}>
						{filterOptions.map((option) => (
							<DropdownMenuRadioItem 
								key={option.value}
								value={option.value} 
								className="DropdownMenuRadioItem"
							>
								<div className="flex items-center gap-2">
									{option.icon && (
										<FontAwesomeIcon
											icon={option.icon}
											className="text-text-2 text-sm"
										/>
									)}
									<span>{option.label}</span>
								</div>
							</DropdownMenuRadioItem>
						))}
					</DropdownMenuRadioGroup>
					{reportId && (
						<>
							<DropdownMenuSeparator className="h-px bg-border my-1" />
							<DropdownMenuItem
								className="DropdownMenuItem cursor-pointer"
								onSelect={handleGeneratePdf}
								disabled={isGeneratingPdf}
							>
								<div className="flex items-center gap-2">
									<FontAwesomeIcon
										icon={icon({ name: "file-pdf", family: "sharp", style: "regular" })}
										className="text-text-2 text-sm"
									/>
									<span>Generate PDF</span>
								</div>
							</DropdownMenuItem>
							{onSchedule && (
								<DropdownMenuItem
									className="DropdownMenuItem cursor-pointer"
									onSelect={handleSchedule}
								>
									<div className="flex items-center gap-2">
										<FontAwesomeIcon
											icon={icon({ name: "clock", family: "sharp", style: "regular" })}
											className="text-text-2 text-sm"
										/>
										<span>Schedule...</span>
									</div>
								</DropdownMenuItem>
							)}
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
	);
};

GeneralSubmenu.propTypes = {
	onFilterChange: PropTypes.func.isRequired,
	currentFilter: PropTypes.string.isRequired,
	reportId: PropTypes.string,
	reportName: PropTypes.string,
	viewFilters: PropTypes.array,
	onSchedule: PropTypes.func,
	triggerClassName: PropTypes.string,
};

export default GeneralSubmenu;

